import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import ReplayIcon from '@mui/icons-material/Replay';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {
    Alert,
    Box,
    Button,
    Chip,
    Divider,
    LinearProgress,
    Paper,
    SvgIconProps,
    Typography,
} from '@mui/material';
import React, { ComponentType, useEffect, useMemo, useState } from 'react';
import {
    ScriptedLessonPhase,
    ScriptedLessonTranscriptEvent,
    useScriptedLessonSession,
} from '../../hooks/useScriptedLessonSession';
import { LessonPlanSessionModel, SessionSlideModel } from '../../object-actions/types/types';
import { AdminIcon, ProfileIcon } from '../../theme/icons';
import { VoiceNavigationIntent } from './voiceNavigation';

interface LessonPlanScriptedSessionProps {
    lessonPlanId?: number | null;
    session: LessonPlanSessionModel;
    isPublished?: boolean;
    updateSlide?: (slideId: number, updates: Partial<SessionSlideModel>) => void;
    updateSession?: (sessionId: number, updates: Partial<LessonPlanSessionModel>) => void;
    onNavigationIntent?: (intent: VoiceNavigationIntent) => void;
    onVoiceActiveChange?: (voiceActive: boolean) => void;
}

const LessonPlanScriptedSession: React.FC<LessonPlanScriptedSessionProps> = ({
    lessonPlanId,
    session,
    isPublished = false,
    updateSlide,
    updateSession,
    onNavigationIntent,
    onVoiceActiveChange,
}) => {
    const [localError, setLocalError] = useState<string | null>(null);

    const {
        voiceActive,
        phase,
        voiceError,
        transcriptEvents,
        currentAiTranscript,
        currentSlideId,
        activeSlideIndex,
        totalSlides,
        startVoiceLesson,
        stopVoiceLesson,
        advanceToNextSlide,
        requestReframe,
        requestRepeat,
    } = useScriptedLessonSession({
        lessonPlanId: lessonPlanId ?? null,
        session,
        onSlideProgress: updateSlide,
        onSessionProgress: updateSession,
        onNavigationIntent,
    });

    useEffect(() => {
        onVoiceActiveChange?.(voiceActive);
    }, [voiceActive, onVoiceActiveChange]);

    useEffect(() => () => onVoiceActiveChange?.(false), [onVoiceActiveChange]);

    const orderedSlides = useMemo(() => {
        const slides = (session.slides || []).filter((slide): slide is SessionSlideModel => Boolean(slide?.id));
        return [...slides].sort((a, b) => (a.slide_number ?? 0) - (b.slide_number ?? 0));
    }, [session.slides]);

    const currentSlide = useMemo(
        () => orderedSlides.find(slide => slide.id === currentSlideId) ?? null,
        [orderedSlides, currentSlideId],
    );

    const slideDisplayPosition = activeSlideIndex >= 0 ? activeSlideIndex + 1 : null;
    const isLastSlide = slideDisplayPosition != null && totalSlides > 0 && slideDisplayPosition === totalSlides;
    const commandsDisabled = phase === 'connecting' || phase === 'processing';
    const showDetails =
        voiceActive ||
        phase === 'completed' ||
        phase === 'error' ||
        transcriptEvents.length > 0 ||
        Boolean(currentAiTranscript);

    const handleStart = async () => {
        setLocalError(null);
        if (!lessonPlanId || !session?.id || !isPublished) {
            setLocalError('The lesson plan must be published before starting the lesson reader.');
            return;
        }
        await startVoiceLesson();
    };

    return (
        <Paper
            elevation={1}
            data-testid="lesson-plan-scripted-session"
            sx={{
                p: 2,
                mb: 2,
                borderRadius: '0 8px 8px 0',
                border: theme => `1px solid ${theme.palette.divider}`,
                borderLeftWidth: 4,
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Lesson Reader <em>(Beta)</em>
                    </Typography>
                    {showDetails && (
                        <Typography variant="body2" color="text.secondary">
                            Reads each question as written and takes notes for your coach. Say
                            &ldquo;reframe&rdquo;, &ldquo;repeat&rdquo;, or &ldquo;next slide&rdquo; &mdash; or use the buttons.
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Chip
                        label={scriptedPhaseLabel(phase, slideDisplayPosition, totalSlides, currentSlide?.title)}
                        color={phase === 'error' ? 'error' : phase === 'completed' ? 'success' : voiceActive ? 'primary' : 'default'}
                        data-attr="lesson-plan-scripted_phase"
                    />
                    {!voiceActive ? (
                        <Button
                            variant="contained"
                            startIcon={<RecordVoiceOverIcon />}
                            onClick={handleStart}
                            disabled={phase === 'connecting'}
                            data-attr="lesson-plan-scripted_start"
                        >
                            Start Lesson Reader
                        </Button>
                    ) : (
                        <>
                            <Button
                                variant="outlined"
                                startIcon={<ReplayIcon />}
                                onClick={() => requestRepeat()}
                                disabled={commandsDisabled}
                                data-attr="lesson-plan-scripted_repeat"
                            >
                                Say it again
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<SwapHorizIcon />}
                                onClick={() => requestReframe()}
                                disabled={commandsDisabled}
                                data-attr="lesson-plan-scripted_reframe"
                            >
                                Reframe
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<SkipNextIcon />}
                                onClick={() => { void advanceToNextSlide(); }}
                                disabled={commandsDisabled || isLastSlide}
                                data-attr="lesson-plan-scripted_next-slide"
                            >
                                Next slide
                            </Button>
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<StopCircleIcon />}
                                onClick={() => { void stopVoiceLesson(); }}
                                data-attr="lesson-plan-scripted_stop"
                            >
                                Stop
                            </Button>
                        </>
                    )}
                </Box>
            </Box>

            {commandsDisabled && (
                <LinearProgress sx={{ mt: 2 }} data-attr="lesson-plan-scripted_progress" />
            )}

            {(localError || voiceError) && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {localError || voiceError}
                </Alert>
            )}

            {phase === 'completed' && (
                <Alert severity="success" sx={{ mt: 2 }}>
                    Session complete. Check your notifications for next steps.
                </Alert>
            )}

            {showDetails && (
                <>
                    <Divider sx={{ mt: 2, mb: 2 }} />

                    {slideDisplayPosition != null && totalSlides > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="overline" color="text.secondary">
                                Slide {slideDisplayPosition} of {totalSlides}
                                {currentSlide?.title ? ` — ${currentSlide.title}` : ''}
                            </Typography>
                        </Box>
                    )}

                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                            Session transcript
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {transcriptEvents.map(event => {
                                const row = transcriptRowForSpeaker(event.speaker);
                                const RowIcon = row.Icon;
                                return (
                                    <Box
                                        key={event.id}
                                        sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}
                                        aria-label={`${row.ariaLabel}: ${event.text}`}
                                    >
                                        <RowIcon
                                            fontSize="small"
                                            sx={{ color: row.iconColor, mt: 0.25, flexShrink: 0 }}
                                            aria-hidden
                                        />
                                        <Typography
                                            variant="body2"
                                            sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                                        >
                                            {event.text}
                                        </Typography>
                                    </Box>
                                );
                            })}
                            {currentAiTranscript ? (
                                <Box
                                    sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}
                                    aria-label={`Lesson reader (speaking): ${currentAiTranscript}`}
                                >
                                    <AdminIcon
                                        fontSize="small"
                                        sx={{ color: 'primary.main', mt: 0.25, flexShrink: 0 }}
                                        aria-hidden
                                    />
                                    <Typography
                                        variant="body2"
                                        color="primary"
                                        sx={{ fontStyle: 'italic', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                                    >
                                        {currentAiTranscript}
                                    </Typography>
                                </Box>
                            ) : null}
                        </Box>
                    </Box>
                </>
            )}
        </Paper>
    );
};

function transcriptRowForSpeaker(speaker: ScriptedLessonTranscriptEvent['speaker']): {
    Icon: ComponentType<SvgIconProps>;
    iconColor: string;
    ariaLabel: string;
} {
    switch (speaker) {
        case 'ai':
            return { Icon: AdminIcon, iconColor: 'primary.main', ariaLabel: 'Lesson reader' };
        case 'participant':
            return { Icon: ProfileIcon, iconColor: 'text.secondary', ariaLabel: 'You' };
        case 'system':
            return { Icon: AdminIcon, iconColor: 'text.disabled', ariaLabel: 'System' };
    }
}

function scriptedPhaseLabel(
    phase: ScriptedLessonPhase,
    slidePosition: number | null,
    totalSlides: number,
    slideTitle?: string,
): string {
    const slidePrefix = slidePosition != null && totalSlides > 0
        ? `Slide ${slidePosition}/${totalSlides}${slideTitle ? `: ${slideTitle}` : ''}`
        : '';
    switch (phase) {
        case 'connecting':
            return 'Connecting';
        case 'reading':
            return slidePrefix ? `Reading ${slidePrefix}` : 'Reading';
        case 'listening':
            return slidePrefix ? `Listening on ${slidePrefix}` : 'Listening';
        case 'processing':
            return 'Updating progress';
        case 'completed':
            return 'Completed';
        case 'error':
            return 'Error';
        default:
            return 'Ready';
    }
}

export default LessonPlanScriptedSession;
